import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Download, Plus, Search } from 'lucide-react';
import * as hr from '../api';
import { useHR, useLookup } from '../HRContext';
import { errorText } from '../../utils';
import StatusPill from '../components/StatusPill';
import { LcKpis } from '../components/Charts';
import { EMPLOYMENT_STATUSES, download, has, titleCase, toCSV } from '../format';
import { hrSubUrl } from '../catalog';
import Loader from '../components/Loader';
import { Select } from '../components/Controls';
import Pagination from '../../components/requisition/Pagination';

// statutory/bank columns checked for every employee
const CHECKS = [['bank_name', 'Bank'], ['tax_identification_number', 'TIN'], ['pension_rsa_pin', 'RSA PIN'], ['nhf_number', 'NHF']];
const missingOf = e => CHECKS.filter(([n]) => !has(e[n])).map(([, l]) => l);

function Check({ value, text }) {
  return has(value) ? <span className="em-ok">✓{text ? ` ${text}` : ''}</span> : <span className="em-miss"><AlertTriangle size={12} /> Missing</span>;
}

export default function EmployeeMaster() {
  const { canManage, notify } = useHR();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState(''); // applied only when Search is clicked
  const [dept, setDept] = useState('');
  const [status, setStatus] = useState('');
  const [site, setSite] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(params.has('missing'));
  const [page, setPage] = useState({ skip: 0, limit: 10 });
  const [loading, setLoading] = useState(true);
  const departments = useLookup('departments') || [];
  const sites = useLookup('sites') || [];

  // the server filters and pages the master list (X-Total-Count gives the full match count), as Manage Employees
  const filters = { search: search || undefined, department_id: dept || undefined, status: status || undefined, site_id: site || undefined,
    missing_statutory: onlyMissing || undefined };
  const filterKey = JSON.stringify(filters);
  const firstPage = () => setPage(p => (p.skip ? { ...p, skip: 0 } : p));
  const filter = set => v => { set(v); firstPage(); };
  const latest = useRef(0);
  const load = () => {
    const ticket = ++latest.current;
    setError(''); setLoading(true);
    hr.listEmployeesPage({ ...filters, skip: page.skip, limit: page.limit })
      .then(r => { if (ticket === latest.current) { setRows(r.items); setTotal(r.total); } })
      .catch(e => { if (ticket === latest.current) setError(errorText(e)); })
      .finally(() => { if (ticket === latest.current) setLoading(false); });
  };
  useEffect(load, [filterKey, page.skip, page.limit]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { hr.getEmployeeStats().then(setStats).catch(() => setStats(null)); }, []);
  const runSearch = ev => {
    ev?.preventDefault();
    const term = q.trim();
    if (term === search && !page.skip) load(); else { setSearch(term); firstPage(); }
  };

  if (error) return <div className="error">{error}</div>;
  if (!rows) return <Loader title="Loading employees…" subtitle="Fetching master data" />;

  const shown = rows.map(e => ({ ...e, _missing: missingOf(e) }));
  // the cards and the Missing Data count cover every employee (server summary), not just this page
  const kpis = [
    ['Total Employees', stats?.total_employees ?? '—', 'blue', 'users'],
    ['Active', stats?.active_employees ?? '—', 'green', 'user-check'],
    ['Exceptions', stats?.statutory_exceptions ?? '—', 'red', 'triangle-alert'],
    ['Sites', stats?.sites_count ?? '—', 'purple', 'building-2'],
  ];

  const exportCSV = async () => {
    let all;
    try { all = (await hr.listEmployees(filters)).map(e => ({ ...e, _missing: missingOf(e) })); } catch (err) { notify(errorText(err), 'bad'); return; }
    download('employee-master.csv', toCSV([
    { label: 'Emp ID', get: e => e.employee_no }, { label: 'Name', get: e => e.full_name }, { label: 'Gender', get: e => titleCase(e.gender) },
    { label: 'Grade', get: e => e.pay_grade }, { label: 'Department', get: e => e.department_name }, { label: 'Job Title', get: e => e.designation_title || e.job_title }, { label: 'Site', get: e => e.site_name },
    { label: 'Type', get: e => titleCase(e.employment_type) }, { label: 'Bank', get: e => e.bank_name }, { label: 'TIN', get: e => e.tax_identification_number },
    { label: 'RSA PIN', get: e => e.pension_rsa_pin }, { label: 'NHF', get: e => e.nhf_number }, { label: 'Status', get: e => titleCase(e.employment_status) },
    { label: 'Missing', get: e => e._missing.join('; ') },
    ], all));
  };

  return (
    <div className="emp-master">
      <LcKpis items={kpis} />

      <div className="panel me-table-panel">
        <div className="toolbar em-filters">
          <form className="me-search" onSubmit={runSearch}>
            <div className="search em-search">
              <Search size={16} />
              <input placeholder="Search name, ID, job title..." value={q} onChange={e => setQ(e.target.value)} />
            </div>
            <button type="submit" className="btn primary" disabled={loading}><Search size={15} /> Search</button>
          </form>
          <Select value={dept} onChange={e => filter(setDept)(e.target.value)}><option value="">All Departments</option>{departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</Select>
          <Select value={status} onChange={e => filter(setStatus)(e.target.value)}><option value="">All Status</option>{EMPLOYMENT_STATUSES.map(s => <option key={s} value={s}>{titleCase(s)}</option>)}</Select>
          <Select value={site} onChange={e => filter(setSite)(e.target.value)}><option value="">All Sites</option>{sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>
          <button type="button" className={`btn ${onlyMissing ? 'danger-on' : ''}`} onClick={() => filter(setOnlyMissing)(!onlyMissing)} aria-pressed={onlyMissing}>
            <AlertTriangle size={15} /> Missing Data ({stats?.statutory_exceptions ?? '…'})
          </button>
          <div className="spacer" />
          <button className="btn" onClick={exportCSV}><Download size={15} /> Export</button>
          {canManage && <Link className="btn primary" to={hrSubUrl('add-employee')}><Plus size={15} /> Add Employee</Link>}
        </div>
        <p className="muted small em-count">{total} employee{total === 1 ? '' : 's'}{onlyMissing ? ' with missing data' : ''}</p>

        <div className="table-wrap">
          <table className="me-table em-table">
            <thead>
              <tr><th>Emp ID</th><th>Name</th><th>Grade</th><th>Department</th><th>Job Title</th><th>Site</th><th>Type</th>
                <th>Bank</th><th>TIN</th><th>RSA PIN</th><th>NHF</th><th>Status</th></tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12}><Loader title="Loading employees…" subtitle={`Page ${Math.floor(page.skip / page.limit) + 1} of ${Math.max(1, Math.ceil(total / page.limit))}`} /></td></tr>
              )}
              {!loading && shown.map(e => (
                <tr key={e.id} className="clickable" onClick={() => navigate(hrSubUrl('employee-360', { id: e.id }))} title="Open Employee 360">
                  <td className="em-id">{e.employee_no}</td>
                  <td><b>{e.full_name}</b><small>{titleCase(e.gender) || '—'}</small></td>
                  <td>{e.pay_grade ? <span className="em-grade">{e.pay_grade}</span> : <span className="muted">—</span>}</td>
                  <td>{e.department_name ? <span className="em-dept">{e.department_name}</span> : <span className="muted">—</span>}</td>
                  <td className="em-trunc" title={e.designation_title || e.job_title || ''}>{e.designation_title || e.job_title || '—'}</td>
                  <td className="em-trunc" title={e.site_name || ''}>{e.site_name || '—'}</td>
                  <td>{titleCase(e.employment_type) || '—'}</td>
                  <td><Check value={e.bank_name} text={e.bank_name} /></td>
                  <td><Check value={e.tax_identification_number} /></td>
                  <td><Check value={e.pension_rsa_pin} /></td>
                  <td><Check value={e.nhf_number} /></td>
                  <td><StatusPill value={e.employment_status} /></td>
                </tr>
              ))}
              {!loading && shown.length === 0 && <tr><td colSpan={12} className="empty">No employees match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination total={total} skip={page.skip} limit={page.limit} onChange={setPage} />
      </div>
    </div>
  );
}
